'use client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronDown, ChevronUp, Package, AlertCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { useLoader } from '../../_helper/LoaderContext';
import { createTestOrder, generateTestReport, getTestResult } from './service/report-test-service';
import { viewDoctor } from '../DoctorManagment/service/doctor-service';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import errorProfile from "../.././assets/images/blank_profile.png";
import { FiPhoneCall } from "react-icons/fi";
import { TfiEmail } from "react-icons/tfi";
import S3ImageLoader from "../../S3ImageLoader";
import { toast } from 'react-toastify';
import ReportList from './ReportList';
import { Modal, Button } from "react-bootstrap";
import { Tooltip } from 'react-tooltip';

// SMART REFERENCE RANGE
// const getReferenceRange = (ranges = [], gender, age) => {
//     if (!Array.isArray(ranges) || ranges.length === 0) return 'N/A';
//     const normalizedGender = (gender || 'male').toLowerCase();
//     const isAdult = (age || 0) >= 18;
//     let match = ranges.find(r => {
//         const rg = (r.gender || 'all').toLowerCase();
//         const matchesGender = rg === normalizedGender || rg === 'all';
//         const matchesAge =
//             (r.ageCategory === 'adult' && isAdult) ||
//             (r.ageCategory === 'child' && !isAdult) ||
//             r.ageCategory === 'all';
//         return matchesGender && matchesAge;
//     });
//     if (!match) {
//         match = ranges.find(r => (r.gender || 'all').toLowerCase() === 'all');
//     }
//     if (!match) return 'N/A';
//     if (match.qualitativeValue !== null && match.qualitativeValue !== undefined && match.qualitativeValue !== '') return match.qualitativeValue.trim();
//     if (match.ratioValue !== null && match.ratioValue !== undefined && match.ratioValue !== '') return match.ratioValue.trim();
//     if (match.scaleValue !== null && match.scaleValue !== undefined && match.scaleValue !== '') return match.scaleValue.trim();
//     if (match.minValue !== null && match.maxValue !== null) return `${match.minValue} - ${match.maxValue}`;
//     if (match.minValue !== null) return `≥ ${match.minValue}`;
//     if (match.maxValue !== null) return `≤ ${match.maxValue}`;
//     if (match.scaleNote) return match.scaleNote.trim();
//     return 'N/A';
// };

// SMART REFERENCE RANGE — Fixed for qualitative, ratio, scale values
const getReferenceRange = (ranges = [], gender, age) => {
    if (!Array.isArray(ranges) || ranges.length === 0) return 'N/A';

    const normalizedGender = (gender || 'male').toLowerCase();
    const isAdult = (age || 0) >= 18;

    // Helper: Find best match with age + gender priority
    const findBestMatch = (filterCondition) => {
        return ranges.find(r => {
            const rg = (r.gender || 'all').toLowerCase();
            const matchesGender = rg === normalizedGender || rg === 'all';
            const matchesAge =
                (r.ageCategory === 'adult' && isAdult) ||
                (r.ageCategory === 'child' && !isAdult) ||
                r.ageCategory === 'all';
            return filterCondition(r) && matchesGender && matchesAge;
        }) ||
            ranges.find(r => {
                const rg = (r.gender || 'all').toLowerCase();
                const matchesGender = rg === 'all';
                return filterCondition(r) && matchesGender;
            });
    };

    // 1. Qualitative value — highest priority, ignore age/gender for matching if possible
    if (ranges.some(r => r.qualitativeValue?.trim())) {
        const match = findBestMatch(r => r.qualitativeValue?.trim());
        if (match?.qualitativeValue?.trim()) {
            return match.qualitativeValue.trim();
        }
    }

    // 2. Ratio value (e.g., "1:80")
    if (ranges.some(r => r.ratioValue?.trim())) {
        const match = findBestMatch(r => r.ratioValue?.trim());
        if (match?.ratioValue?.trim()) {
            return match.ratioValue.trim();
        }
    }

    // 3. Scale value (e.g., "Trace", "1+", "2+")
    if (ranges.some(r => r.scaleValue?.trim())) {
        const match = findBestMatch(r => r.scaleValue?.trim());
        if (match?.scaleValue?.trim()) {
            return match.scaleValue.trim();
        }
    }

    // 4. Scale note (e.g., "Seen", "Not Seen")
    if (ranges.some(r => r.scaleNote?.trim())) {
        const match = findBestMatch(r => r.scaleNote?.trim());
        if (match?.scaleNote?.trim()) {
            return match.scaleNote.trim();
        }
    }

    // 5. Only now apply strict numeric range with age/gender logic
    const numericMatch = ranges.find(r => {
        const rg = (r.gender || 'all').toLowerCase();
        const matchesGender = rg === normalizedGender || rg === 'all';
        const matchesAge =
            (r.ageCategory === 'adult' && isAdult) ||
            (r.ageCategory === 'child' && !isAdult) ||
            r.ageCategory === 'all';
        return matchesGender && matchesAge && (r.minValue !== null || r.maxValue !== null);
    });

    if (numericMatch) {
        if (numericMatch.minValue !== null && numericMatch.maxValue !== null) {
            return `${numericMatch.minValue} - ${numericMatch.maxValue}`;
        }
        if (numericMatch.minValue !== null) {
            return `≥ ${numericMatch.minValue}`;
        }
        if (numericMatch.maxValue !== null) {
            return `≤ ${numericMatch.maxValue}`;
        }
    }

    // Final fallback to any "all" entry
    const fallback = ranges.find(r => (r.gender || 'all').toLowerCase() === 'all');
    if (fallback) {
        if (fallback.qualitativeValue?.trim()) return fallback.qualitativeValue.trim();
        if (fallback.ratioValue?.trim()) return fallback.ratioValue.trim();
        if (fallback.scaleValue?.trim()) return fallback.scaleValue.trim();
        if (fallback.scaleNote?.trim()) return fallback.scaleNote.trim();
        if (fallback.minValue !== null && fallback.maxValue !== null) return `${fallback.minValue} - ${fallback.maxValue}`;
    }

    return 'N/A';
};

const checkOutOfRange = (value, range) => {
    if (!value || !range) return false;

    const num = parseFloat(value);
    if (isNaN(num)) return false;

    // Match "10 - 20"
    const rangeMatch = range.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)$/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        return num < min || num > max;
    }

    // Match "≥ 10"
    const minMatch = range.match(/^≥\s*(\d+\.?\d*)$/);
    if (minMatch) {
        const min = parseFloat(minMatch[1]);
        return num < min;
    }

    // Match "≤ 20"
    const maxMatch = range.match(/^≤\s*(\d+\.?\d*)$/);
    if (maxMatch) {
        const max = parseFloat(maxMatch[1]);
        return num > max;
    }

    return false;
};

const resolveClinicDoctor = async (doctor) => {
    if (!doctor || doctor.isClinicDoctor !== true) return doctor;
    if (doctor.signature || doctor.signatureImage) return doctor;

    try {
        const detailRes = await viewDoctor(doctor.doctorId || doctor._id, { isClinicDoctor: true });
        const detail = detailRes?.data?.data;
        if (!detailRes?.data?.success || !detail) return doctor;
        const filledDetail = Object.fromEntries(
            Object.entries(detail).filter(([, value]) => value !== null && value !== undefined && value !== "")
        );
        return { ...doctor, ...filledDetail, isClinicDoctor: true };
    } catch (error) {
        console.log(error);
        return doctor;
    }
};

const clinicDoctorPayload = (doctor) => {
    if (!doctor || doctor.isClinicDoctor !== true) return {};

    const name = doctor.name || `${doctor.fname || ""} ${doctor.lname || ""}`.trim();
    return {
        isClinicDoctor: true,
        doctor: {
            doctorId: doctor.doctorId || doctor._id || "",
            name,
            fname: doctor.fname || "",
            lname: doctor.lname || "",
            email: doctor.email || "",
            mobile: doctor.mobile || doctor.mobileNumber || "",
            mobileNumber: doctor.mobileNumber || doctor.mobile || "",
            specialization: doctor.specialization || doctor.degree || "",
            degree: doctor.degree || doctor.specialization || "",
            hospitalName: doctor.hospitalName || "",
            address: doctor.address || "",
            signature: doctor.signature || doctor.signatureImage || "",
            signatureImage: doctor.signatureImage || doctor.signature || "",
            signatureName: doctor.signatureName || name,
            signatureQualification: doctor.signatureQualification || doctor.degree || doctor.specialization || "",
            profileImage: doctor.profileImage || doctor.profilePicture || "",
            commissionPercentage: doctor.commissionPercentage ?? doctor.commissionPercent ?? 0,
            isClinicDoctor: true,
        },
    };
};

export default function AddTestResult() {
    const { id } = useParams();
    const [apiData, setApiData] = useState(null);
    const [expandedTests, setExpandedTests] = useState({});
    const [expandedPackages, setExpandedPackages] = useState({});
    const { setLoading } = useLoader();
    const [testNotes, setTestNotes] = useState({});
    const [highlightedParams, setHighlightedParams] = useState({});
    const navigate = useNavigate();
    const [data, setData] = useState({})
    const [showResult, setShowResult] = useState(false);
    const [completedTests, setCompletedTests] = useState(new Set());
    const handleCloseResult = () => {
        setShowResult(false)
        navigate(`${process.env.PUBLIC_URL}/test-list`);
    };

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        shouldUnregister: false,
    });

    useEffect(() => {
        fetchTestData();
    }, [id]);

    const fetchTestData = async () => {
        setLoading(true);
        try {
            const res = await getTestResult(id);
            if (res.data.success) {
                setApiData(res.data);

                const initialNotes = {};
                const initialHighlights = {};
                const testExpansion = {};
                const packageExpansion = {};

                res.data.data.allTests.forEach(test => {
                    // Notes
                    if (test.reportNote) {
                        initialNotes[test._id] = test.reportNote;
                    }


                    // Auto-expand all tests inside packages
                    if (test.isPackage) {
                        testExpansion[test._id] = true;
                        if (test.packageId) {
                            packageExpansion[test.packageId._id] = true;
                        }
                    }

                    // Traverse parameters to pre-fill values
                    test.parameterGroups?.forEach(group => {
                        group.parameters?.forEach(param => {
                            const fieldName = test.isPackage
                                ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                                : `standalone_${test._id}_param_${param.parameterId}`;

                            // Pre-fill existing result values
                            if (param.resultValue) {
                                setValue(fieldName, param.resultValue);
                            }

                            // Set highlight state
                            if (param.isHighlight !== undefined) {
                                initialHighlights[fieldName] = param.isHighlight;
                            }
                        });
                    });

                    const preCompleted = new Set();
                    let allFilled = true;
                    test.parameterGroups?.forEach(group => {
                        group.parameters?.forEach(param => {
                            if (!param.resultValue || param.resultValue.trim() === '') {
                                allFilled = false;
                            }
                        });
                    });

                    if (allFilled) {
                        preCompleted.add(test._id);
                    }
                    setCompletedTests(preCompleted);
                });

                setTestNotes(initialNotes);
                setHighlightedParams(initialHighlights);
                setExpandedTests(prev => ({ ...prev, ...testExpansion }));
                setExpandedPackages(prev => ({ ...prev, ...packageExpansion }));
            }
        } catch (error) {
            console.error("Error:", error);
            toast.warning("Failed to load test data");
        } finally {
            setLoading(false);
        }
    };

    const importFileButton = (
        <button
            type="button"
            className="btn btn-primary"
            onClick={() => toast.error("You are not having permission to do this task.")}
        >
            Import File
        </button>
    );

    if (!apiData?.data) {
        return (
            <Container fluid>
                <Breadcrumbs mainTitle="Add Test Result" parent="Home" subParents="Test List" title="Add Test Result" action={importFileButton} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        );
    }

    const patient = apiData.data?.patient || {};
    const allTests = apiData.data?.allTests || [];
    const gender = (patient.gender || 'male').toLowerCase();
    const age = parseInt(patient.age) || 0;

    const toggleTest = (testId) => {
        setExpandedTests(prev => ({ ...prev, [testId]: !prev[testId] }));
    };

    const togglePackage = (packageId) => {
        setExpandedPackages(prev => ({ ...prev, [packageId]: !prev[packageId] }));
    };

    const toggleHighlight = (fieldName) => {
        setHighlightedParams(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    // const saveSingleTest = async (testId) => {
    //     const test = allTests.find(t => t._id === testId);
    //     if (!test) return;

    //     setLoading(true);
    //     try {
    //         const values = getValues();

    //         const payload = {
    //             allTests: [{
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }]
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             toast.success(res.data.message || "Test saved successfully!");
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning("Failed to save test");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleResultChange = (fieldName, value, range) => {
        const isOut = checkOutOfRange(value, range);
        
        setHighlightedParams(prev => ({
            ...prev,
            [fieldName]: isOut ? true : false
        }));
    };

    const saveSingleTest = async (testId) => {
        const test = allTests.find(t => t._id === testId);
        if (!test) return;

        // Collect all field names for this test
        const fieldNames = [];
        test.parameterGroups?.forEach(group => {
            group.parameters?.forEach(param => {
                const fieldName = test.isPackage
                    ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                    : `standalone_${test._id}_param_${param.parameterId}`;
                fieldNames.push(fieldName);
            });
        });

        // Trigger validation only for this test's fields
        const isValid = await trigger(fieldNames);

        if (!isValid) {
            toast.warning("Please fill all required fields in this test!");
            return;
        }

        setLoading(true);
        try {
            const values = getValues();
            const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);

            const payload = {
                ...clinicDoctorPayload(reportDoctor),
                allTests: [{
                    _id: test._id,
                    parameterGroups: test.parameterGroups.map(group => ({
                        _id: group._id,
                        parameters: group.parameters.map(param => {
                            const fieldName = test.isPackage
                                ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                                : `standalone_${test._id}_param_${param.parameterId}`;

                            return {
                                _id: param._id,
                                parameterId: param.parameterId,
                                name: param.name,
                                resultValue: (values[fieldName] || "").toString().trim(),
                                isHighlight: !!highlightedParams[fieldName]
                            };
                        })
                    })),
                    reportNote: testNotes[test._id] || ""
                }]
            };

            const res = await createTestOrder(id, payload);
            if (res.data.success) {
                toast.success(res.data.message || "Test saved successfully!");
            }
        } catch (error) {
            console.error(error);
            toast.warning("Failed to save test");
        } finally {
            setLoading(false);
        }
    };

    // const saveSingleTest = async (testId) => {
    //     const test = allTests.find(t => t._id === testId);
    //     if (!test) return;

    //     const fieldNames = [];
    //     test.parameterGroups?.forEach(group => {
    //         group.parameters?.forEach(param => {
    //             const fieldName = test.isPackage
    //                 ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                 : `standalone_${test._id}_param_${param.parameterId}`;
    //             fieldNames.push(fieldName);
    //         });
    //     });

    //     const isValid = await trigger(fieldNames);
    //     if (!isValid) {
    //         toast.warning("Please fill all required fields in this test!");
    //         return;
    //     }

    //     setLoading(true);
    //     try {
    //         const values = getValues();

    //         const payload = {
    //             allTests: [{
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }]
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             toast.success(res.data.message || "Test saved successfully!");

    //             // Mark this test as completed (since save succeeded and validation passed)
    //             setCompletedTests(prev => {
    //                 const newSet = new Set(prev);
    //                 newSet.add(test._id);
    //                 return newSet;
    //             });

    //             // Check if ALL tests are now completed
    //             const totalTests = allTests.length;
    //             const completedCount = [...completedTests].length + 1; // +1 for current

    //             if (completedCount === totalTests) {
    //                 // toast.info("All tests completed! Generating report...");
    //                 await generateReport();
    //             }
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning(error.response?.data?.message || "Failed to save test");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // const onGlobalSubmit = async () => {
    //     const isValid = await trigger();
    //     if (!isValid) {
    //         toast.warning("Please fill all required fields!");
    //         return;
    //     }

    //     const values = getValues();
    //     setLoading(true);
    //     try {
    //         const payload = {
    //             allTests: allTests.map(test => ({
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }))
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             // toast.success(res.data.message || "All results saved successfully!");
    //             await generateReport();
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning("Failed to submit results");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const onGlobalSubmit = async () => {
        const isValid = await trigger(); // This triggers validation on ALL registered fields

        if (!isValid) {
            toast.warning("Please fill all required fields before submitting!");
            return;
        }

        const values = getValues();
        setLoading(true);
        try {
            const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);
            const payload = {
                ...clinicDoctorPayload(reportDoctor),
                allTests: allTests.map(test => ({
                    _id: test._id,
                    parameterGroups: test.parameterGroups.map(group => ({
                        _id: group._id,
                        parameters: group.parameters.map(param => {
                            const fieldName = test.isPackage
                                ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                                : `standalone_${test._id}_param_${param.parameterId}`;

                            return {
                                _id: param._id,
                                parameterId: param.parameterId,
                                name: param.name,
                                resultValue: (values[fieldName] || "").toString().trim(),
                                isHighlight: !!highlightedParams[fieldName]
                            };
                        })
                    })),
                    reportNote: testNotes[test._id] || ""
                }))
            };

            const res = await createTestOrder(id, payload);
            if (res.data.success) {
                // toast.success("All results saved successfully!");
                await generateReport();
            }
        } catch (error) {
            console.error(error);
            toast.warning(error.response?.data?.message || "Failed to submit results");
        } finally {
            setLoading(false);
        }
    };

    const generateReport = async () => {
        // const testIds = allTests
        const isValid = await trigger(); // This triggers validation on ALL registered fields

        if (!isValid) {
            toast.warning("Please fill all required fields before submitting!");
            return;
        }
        const testIds = allTests.map(test => test.testId)
        console.log(testIds)
        const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);
        const payload = {
            testOrderId: id,
            testId: testIds,
            ...clinicDoctorPayload(reportDoctor),
        };
        const reportData = { _id: id, testId: testIds };
        setData(reportData)
        setLoading(true);
        try {
            const res = await generateTestReport(payload);
            if (res.data.success) {
                toast.success("Report generated successfully!");
                setShowResult(true)
                // navigate(`${process.env.PUBLIC_URL}/test-list`);
            }
        } catch (error) {
            toast.warning(error.response?.data?.message || "Failed to generate report");
        } finally {
            setLoading(false);
        }
    };

    const scrollToFirstError = (errors) => {
        if (Object.keys(errors).length > 0) {
            const firstErrorKey = Object.keys(errors)[0];
            const element = document.querySelector(`[name="${firstErrorKey}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
            }
        }
    };

    const standaloneTests = allTests.filter(t => !t.isPackage);
    const packageMap = {};
    allTests.forEach(test => {
        if (test.isPackage && test.packageId) {
            const pkgId = test.packageId._id;
            if (!packageMap[pkgId]) {
                packageMap[pkgId] = { packageName: test.packageName, tests: [] };
            }
            packageMap[pkgId].tests.push(test);
        }
    });

    return (
        <>
            <Breadcrumbs mainTitle="Add Test Result" parent="Home" subParents="Test List" title="Add Test Result" action={importFileButton} />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <div className="py-4">
                            <Card className="border-success border-2 mb-4">
                                <div className="p-3">
                                    <div className="d-flex align-items-center gap-4">
                                        <S3ImageLoader fileName={patient?.profile_picture} fallbackSrc={errorProfile} className="img-60 rounded-circle" alt="Patient" />
                                        <div>
                                            <h5 className="mb-0 fw-bold">{patient.name}</h5>
                                            <small className="text-muted text-capitalize">{patient.gender}, Age {patient.age}</small>
                                        </div>
                                        <div className="ms-auto d-flex gap-4">
                                            {patient.contact !== "" && (
                                                <div className="d-flex align-items-center gap-2"><FiPhoneCall /> {patient.contact}</div>
                                            )}
                                            {patient.email !== "" && (
                                                <div className="d-flex align-items-center gap-2"><TfiEmail /> {patient.email}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <form onSubmit={(e) => { e.preventDefault(); onGlobalSubmit(); }}>
                                <div className="space-y-4">

                                    {/* Standalone Tests */}
                                    {standaloneTests.map(test => (
                                        <div key={test._id} className="card shadow rounded-4 overflow-hidden border border-success">
                                            <div className="card-header p-3 d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleTest(test._id)}>
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="badge bg-primary px-4 py-2 fw-bold">{test.code}</span>
                                                    <h5 className="mb-0">{test.name}</h5>
                                                </div>
                                                {expandedTests[test._id] ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
                                            </div>

                                            {expandedTests[test._id] && (
                                                <>
                                                    <div onClick={e => e.stopPropagation()} className="card-body p-4">
                                                        <table className="table table-sm">
                                                            <thead>
                                                                <tr className="text-muted">
                                                                    <th>Parameter</th>
                                                                    <th>Result *</th>
                                                                    <th>Reference Range</th>
                                                                    <th>Unit</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {test.parameterGroups?.map(group => group.parameters?.map(param => {
                                                                    const originalParam = test.testId?.parameterGroups
                                                                        ?.flatMap(g => g.parameters || [])
                                                                        .find(p => p.parameterId === param.parameterId);
                                                                    const range = getReferenceRange(
                                                                        originalParam?.referenceRanges || param.referenceRanges,
                                                                        gender,
                                                                        age
                                                                    );
                                                                    const fieldName = `standalone_${test._id}_param_${param.parameterId}`;

                                                                    return (
                                                                        <tr key={param._id}>
                                                                            <td className="fw-medium align-middle">{param.name}</td>
                                                                            <td className="align-middle">
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!highlightedParams[fieldName]}
                                                                                        onChange={() => toggleHighlight(fieldName)}
                                                                                        disabled={apiData.data.status === "Completed"}
                                                                                        data-tooltip-id="name-tooltips"
                                                                                    //  id={`highlight-${fieldName}`}
                                                                                    />
                                                                                    <Tooltip id="name-tooltips" place="top" content="Highlight this field" />
                                                                                    <input
                                                                                        {...register(fieldName, { required: "Required" })}
                                                                                        type="text"
                                                                                        className={`form-control form-control-sm ${errors[fieldName] ? 'border-danger' : ''}`}
                                                                                        style={{ maxWidth: '160px' }}
                                                                                        disabled={apiData.data.status === "Completed"}
                                                                                        placeholder="Enter result"
                                                                                        onChange={(e) => {
                                                                                            const value = e.target.value;
                                                                                    
                                                                                            // Call RHF onChange
                                                                                            setValue(fieldName, value, { shouldValidate: true });
                                                                                    
                                                                                            // Auto highlight logic
                                                                                            handleResultChange(fieldName, value, range);
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                {errors[fieldName] && (
                                                                                    <small className="text-danger d-flex align-items-center gap-1 mt-1">
                                                                                        <AlertCircle size={14} /> Required
                                                                                    </small>
                                                                                )}
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <span className="fw-bold text-success">{range}</span>
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <span className="fw-bold">{originalParam?.unitValue || param.unitValue || '-'}</span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }))}
                                                            </tbody>
                                                        </table>

                                                        <ReactQuill
                                                            theme="snow"
                                                            value={testNotes[test._id] || ''}
                                                            onChange={(content) => setTestNotes(prev => ({ ...prev, [test._id]: content }))}
                                                            placeholder="Add notes..."
                                                            className="mt-4"
                                                            readOnly={apiData.data.status === "Completed"}
                                                        />
                                                    </div>

                                                    <div className="p-3 text-end border-top">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); saveSingleTest(test._id); }}
                                                            className="btn btn-primary btn-sm"
                                                            disabled={apiData.data.status === "Completed"}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}

                                    {/* Packages */}
                                    {Object.entries(packageMap).map(([packageId, pkg]) => (
                                        <div key={packageId} className="card shadow-lg rounded-4 overflow-hidden border border-success">
                                            <div
                                                className="card-header p-3 d-flex justify-content-between align-items-center cursor-pointer"
                                                onClick={() => togglePackage(packageId)}
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <Package size={32} />
                                                    <h4 className="mb-0">{pkg.packageName}</h4>
                                                    <span className="badge bg-primary ms-2">{pkg.tests.length} Tests</span>
                                                </div>
                                                {expandedPackages[packageId] ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
                                            </div>

                                            {expandedPackages[packageId] && (
                                                <div className="p-4">
                                                    {pkg.tests.map(test => (
                                                        <div key={test._id} className="bg-white rounded-4 shadow mb-4 border">
                                                            <div
                                                                className="p-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
                                                                onClick={() => toggleTest(test._id)}
                                                            >
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <span className="badge bg-primary">{test.code}</span>
                                                                    <h6 className="mb-0">{test.name}</h6>
                                                                </div>
                                                                {expandedTests[test._id] ? <ChevronUp /> : <ChevronDown />}
                                                            </div>

                                                            {expandedTests[test._id] && (
                                                                <>
                                                                    <div onClick={e => e.stopPropagation()} className="p-4">
                                                                        <table className="table table-sm">
                                                                            <thead>
                                                                                <tr className="text-muted">
                                                                                    <th>Parameter</th>
                                                                                    <th>Result *</th>
                                                                                    <th>Reference Range</th>
                                                                                    <th>Unit</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {test.parameterGroups?.map(group => group.parameters?.map(param => {
                                                                                    const originalParam = test.testId?.parameterGroups
                                                                                        ?.flatMap(g => g.parameters || [])
                                                                                        .find(p => p.parameterId === param.parameterId);
                                                                                    const range = getReferenceRange(
                                                                                        originalParam?.referenceRanges || param.referenceRanges,
                                                                                        gender,
                                                                                        age
                                                                                    );
                                                                                    const fieldName = `pkg_${packageId}_test_${test._id}_param_${param.parameterId}`;

                                                                                    return (
                                                                                        <tr key={param._id}>
                                                                                            <td className="fw-medium align-middle">{param.name}</td>
                                                                                            <td className="align-middle">
                                                                                                <div className="d-flex align-items-center gap-2">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        className="form-check-input"
                                                                                                        checked={!!highlightedParams[fieldName]}
                                                                                                        onChange={() => toggleHighlight(fieldName)}
                                                                                                        disabled={apiData.data.status === "Completed"}
                                                                                                        data-tooltip-id="name-tooltipPackage"
                                                                                                    />
                                                                                                    <Tooltip id="name-tooltipPackage" place="top" content="Highlight this field" />
                                                                                                    <input
                                                                                                        {...register(fieldName, { required: "Required" })}
                                                                                                        type="text"
                                                                                                        className={`form-control form-control-sm ${errors[fieldName] ? 'border-danger' : ''}`}
                                                                                                        style={{ maxWidth: '160px' }}
                                                                                                        disabled={apiData.data.status === "Completed"}
                                                                                                        placeholder="Enter result"
                                                                                                        onChange={(e) => {
                                                                                                            const value = e.target.value;
                                                                                                    
                                                                                                            // Call RHF onChange
                                                                                                            setValue(fieldName, value, { shouldValidate: true });
                                                                                                    
                                                                                                            // Auto highlight logic
                                                                                                            handleResultChange(fieldName, value, range);
                                                                                                        }}
                                                                                                    />
                                                                                                </div>
                                                                                                {errors[fieldName] && (
                                                                                                    <small className="text-danger d-flex align-items-center gap-1 mt-1">
                                                                                                        <AlertCircle size={14} /> Required
                                                                                                    </small>
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="align-middle">
                                                                                                <span className="fw-bold text-success">{range}</span>
                                                                                            </td>
                                                                                            <td className="align-middle">
                                                                                                <span className="fw-bold">{originalParam?.unitValue || param.unitValue || '-'}</span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                }))}
                                                                            </tbody>
                                                                        </table>

                                                                        <ReactQuill
                                                                            theme="snow"
                                                                            value={testNotes[test._id] || ''}
                                                                            onChange={(content) => setTestNotes(prev => ({ ...prev, [test._id]: content }))}
                                                                            placeholder="Add notes..."
                                                                            className="mt-3"
                                                                            readOnly={apiData.data.status === "Completed"}
                                                                        />
                                                                    </div>

                                                                    <div className="p-3 text-end border-top">
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => { e.stopPropagation(); saveSingleTest(test._id); }}
                                                                            className="btn btn-primary btn-sm"
                                                                            disabled={apiData.data.status === "Completed"}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="d-flex justify-content-end gap-3 my-5">
                                    {apiData.data.status === "Submitted" ? (
                                        <button
                                            type="button"
                                            className="btn btn-primary px-5"
                                            onClick={generateReport}
                                        >
                                            Genrate Report
                                        </button>

                                    ) : (
                                        <button
                                            type="submit"
                                            className="btn btn-primary px-5"
                                            disabled={apiData.data.status === "Completed"}
                                        >
                                            Submit All Results
                                        </button>
                                    )}
                                    <Link to={`${process.env.PUBLIC_URL}/test-list`}>
                                        <button type="button" className="btn btn-secondary">Cancel</button>
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Modal show={showResult} onHide={handleCloseResult} centered scrollable>
                <ReportList setUploadModelOpen={handleCloseResult} toggleUploadModel={handleCloseResult} data={data} />
            </Modal>
        </>
    );
}